// PRD 14 I5 — Tenis10 children as support sees them (a family's, or an account's on the player page): the profile, the level,
// the state with who and when (the Claim's account, the hide, the forward, the claims that ended) and the actions the API
// allows each child — the claim into an account and the hide for an unclaimed child, the cancel and the move for a claimed
// one, the show-again for a hidden one nobody holds. PRD 16 LR12 (LR10 verdict P-A): a child the parent added in Sportya too
// (an account's page), and every child's passport — with support's activation for a child an account holds without one.
// RELEASE-TENIS10 § 11 P5: the correction of the data of a child an account holds under 16 (the parent reads it only).
// RELEASE-TENIS10 § 11 P78: every child with its own number (and its tenis10.ro id), in place of the shortened profile id.
import React from "react"
import { Link } from "react-router-dom"
import { CLAIM_HOW, HIDDEN_BY, accountLabel, childBadge, childNumber, formatDate, formatDateTime, levelText } from "./claimsLabels"
import { passportBadge, passportLine, usePassportPayments } from "./passportLabels"
import LegacyId from "./LegacyId"

const initials = child => `${(child.firstName || "?").charAt(0)}${(child.lastName || "").charAt(0)}`.toUpperCase()
const shortId = id => `${String(id).slice(0, 4)}…${String(id).slice(-4)}`

/** RELEASE-TENIS10 § 11 P78: the child's number beside its name, as an account's „#990044” reads; under it the birth year and,
 * for a child from tenis10.ro, its tenis10.ro id (P103: „T10#31877”). A read without the number keeps the shortened profile id. */
export function ChildName({ child }) {
  const number = childNumber(child)
  const details = number != null ? [`born ${child.birthYear}`] : [`#${shortId(child.profileId)}`, `born ${child.birthYear}`]
  const legacy = number != null && child.legacyPlayerId != null
  return <div><div className="font-weight-medium">{child.firstName} {child.lastName}
    {number != null && <> <span className="text-muted font-weight-normal">#{number}</span></>}</div>
    <div className="text-muted font-size-12">{details.join(" · ")}{legacy && <> · <LegacyId id={child.legacyPlayerId} /></>}</div></div>
}

const AccountLink = ({ account }) => (account?.accountId
  ? <Link to={`/accounts/players/${account.accountId}`}>{accountLabel(account)}</Link>
  : <span>{accountLabel(account)}</span>)

/** Who holds the child and since when, who hid it, where it was forwarded — one muted line under the badge. */
function StateDetail({ child }) {
  if (child.origin === "sportya") return <div className="text-muted font-size-12">Added by the parent · {formatDate(child.addedAt)}</div>
  if (child.hidden) {
    const by = child.hidden.by === "support" && child.hidden.admin ? child.hidden.admin.name || `#${child.hidden.admin.userId}` : HIDDEN_BY[child.hidden.by]
    return <div className="text-muted font-size-12">{formatDateTime(child.hidden.at)} · {by}
      {child.claim && <> · <AccountLink account={child.claim.account} />{child.claim.account?.deleted ? " (deleted)" : ""}</>}</div>
  }
  if (child.claim) {
    return <div className="text-muted font-size-12"><AccountLink account={child.claim.account} /> · {formatDate(child.claim.at)}
      {child.claim.by && <> · {child.claim.by.name || `#${child.claim.by.userId}`}</>}</div>
  }
  if (child.forward) {
    return <div className="text-muted font-size-12">To {child.forward.email} · {formatDate(child.forward.sentAt)}{child.forward.sends > 1 ? ` · sent ${child.forward.sends} times` : ""}
      {child.forward.bounce ? ` · bounced ${formatDate(child.forward.bounce.at)}` : ""}</div>
  }
  return null
}

/** The claims that ended, the latest first: a cancel or a move, with the admin. */
const History = ({ child }) => (child.history?.length ? <ul className="tenis10-claims-history list-unstyled mb-0 mt-1">
  {child.history.map(ended => <li key={`${ended.at}-${ended.account?.accountId}`} className="text-muted font-size-12">
    {ended.moved
      ? <>Moved from {accountLabel(ended.account)} to {accountLabel(ended.moved.to)} · {formatDate(ended.moved.at)} · {ended.moved.by?.name || `#${ended.moved.by?.userId}`}</>
      : <>Claim ({CLAIM_HOW[ended.how] || ended.how}) by {accountLabel(ended.account)} cancelled · {formatDate(ended.cancelled?.at)} · {ended.cancelled?.by?.name || `#${ended.cancelled?.by?.userId}`}</>}
  </li>)}
</ul> : null)

/** The passport: its state, and the window with where it came from — none for a 16+ history (the player's own). */
function PassportCell({ child, payment }) {
  if (child.adult || !child.passport) return <span className="text-muted">—</span>
  const [tone, text] = passportBadge(child.passport)
  return <><span className={`badge badge-soft-${tone}`}>{text}</span>
    <div className="text-muted font-size-12">{passportLine(child.passport, payment)}</div></>
}

const BUTTONS = {
  activatePassport: ["Activate passport", "btn-primary"],
  // RELEASE-TENIS10 § 11 P5 — the parent reads the child's data only: support corrects it on request
  editData: ["Edit child data", "btn-light"],
  claim: ["Claim into account", "btn-primary"],
  hide: ["Hide", "btn-light"],
  cancelClaim: ["Cancel claim", "btn-soft-danger"],
  move: ["Move to another account", "btn-light"],
  show: ["Show again", "btn-light"],
}

export default function ChildrenTable({ rows, onAction, label = "Children", showFamily = false }) {
  const payments = usePassportPayments(rows)
  return <div className="table-responsive"><table className="table tenis10-claims-table mb-0" aria-label={label}>
    <thead className="thead-light"><tr>
      <th scope="col">Profile</th>{showFamily && <th scope="col">Legacy family</th>}<th scope="col">Level</th><th scope="col">Passport</th>
      <th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th>
    </tr></thead>
    <tbody>{rows.map(child => {
      const [tone, text] = childBadge(child)
      return <tr key={child.profileId}>
        <td><div className="d-flex align-items-center tenis10-claims-kid">
          <span className={`tenis10-claims-avatar level-${child.tenis10Level || "none"}`} aria-hidden="true">{initials(child)}</span>
          <ChildName child={child} />
        </div></td>
        {showFamily && <td data-label="Legacy family">{child.family
          ? <Link to={`/accounts/tenis10-claims/families/${child.family.invitationId}`}><LegacyId id={child.family.legacyUserId} /></Link>
          : child.origin === "sportya" ? <span className="text-muted">— · added in Sportya</span> : "—"}</td>}
        <td data-label="Level">{levelText(child)}</td>
        <td data-label="Passport"><PassportCell child={child} payment={payments[child.profileId]} /></td>
        <td data-label="Status"><span className={`badge badge-soft-${tone}`}>{text}</span><StateDetail child={child} /><History child={child} /></td>
        <td data-label=""><div className="d-flex flex-wrap tenis10-claims-actions">
          {(child.actions || []).map(action => {
            const [caption, style] = BUTTONS[action] || [action, "btn-light"]
            const primary = action === "move" && child.state === "hidden" ? "btn-primary" : style
            return <button key={action} type="button" className={`btn btn-sm ${primary}`}
              aria-label={`${caption} — ${child.firstName} ${child.lastName}`} onClick={() => onAction(action, child)}>{caption}</button>
          })}
        </div></td>
      </tr>
    })}</tbody>
  </table></div>
}
